import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import { useUserAuth } from "../../context/UserAuthContext";
import { useFeatureFlags } from "../../context/FeatureFlagsContext";
import SearchFilter from "../common/SearchFilter";
import {
  ChevronDown,
  ChevronUp,
  Heart,
  LogOut,
  ShoppingBag,
  Store,
  UserRound,
} from "lucide-react";
import CartDrawer from "../common/CartDrawer";
import { useMediaQuery } from "react-responsive";
import { loadStorefrontPublic } from "../../services/storefrontPublicLoader";
import { useVendorStore } from "../../context/VendorStoreContext";

const Header = () => {
  const { cart, updateQuantity, clearCart, cartDrawerOpen, setCartDrawerOpen } = useCart();
  const { isAuthenticated: isVendorLoggedIn, logout: logoutVendor } = useAuth();
  const { isAuthenticated: isUserLoggedIn, logout: logoutUser } = useUserAuth();
  const { wishlistEnabled } = useFeatureFlags();
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const location = useLocation();
  const isActive = (path) => location.pathname.startsWith(path);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [accountMenuPos, setAccountMenuPos] = useState({ top: 0, right: 0 });
  const accountMenuRef = useRef(null);
  const accountButtonRef = useRef(null);
  const accountDropdownRef = useRef(null);
  const [storefrontVendor, setStorefrontVendor] = useState(null);
  const [loadingStorefront, setLoadingStorefront] = useState(true);
  const { vendorStore, storeSlug: contextSlug } = useVendorStore();

  // On explicit platform-browsing pages, never use a vendor slug.
  // On transitional pages (login, wishlist, etc.) fall back to sessionStorage
  // so the Header keeps the right vendor links after leaving /:storeSlug/*.
  const isExplicitPlatformPage =
    location.pathname === "/" ||
    location.pathname === "/shop" ||
    location.pathname.startsWith("/shop/");
  const storeSlug =
    contextSlug ||
    (!isExplicitPlatformPage
      ? (sessionStorage.getItem("mmd_storeSlug") || null)
      : null);

  // When in vendor-slug mode, NEVER show the superuser logo — show vendor's own logo or their initial.
  // Only fall back to storefrontVendor (superuser) when NOT in any vendor slug URL.
  const logoSrc = vendorStore
    ? (vendorStore.storeLogo?.trim() || null)
    : (storefrontVendor?.storeLogo || null);
  const logoAlt = vendorStore?.storeName || storefrontVendor?.storeName || "Store";

  // Nav destinations: prefix with slug when inside a vendor store
  const rootLink = storeSlug ? `/${storeSlug}` : "/";
  const shopLink = storeSlug ? `/${storeSlug}` : "/shop";
  const aboutLink = storeSlug ? `/${storeSlug}/about` : "/about";
  const contactLink = storeSlug ? `/${storeSlug}/contact` : "/contact";

  useEffect(() => {
    let mounted = true;

    const loadStorefront = async () => {
      try {
        setLoadingStorefront(true);

        const res = await loadStorefrontPublic({ force: true });

        if (!mounted) return;

        setStorefrontVendor(res?.singleVendor ? res.vendor : null);
      } catch {
        if (mounted) {
          setStorefrontVendor(null);
        }
      } finally {
        if (mounted) {
          setLoadingStorefront(false);
        }
      }
    };

    loadStorefront();

    return () => {
      mounted = false;
    };
  }, [location.pathname]);

  const handleLogout = async () => {
    if (isVendorLoggedIn) {
      await logoutVendor();
    }

    if (isUserLoggedIn) {
      await logoutUser();
    }

    clearCart();
  };

  useEffect(() => {
    if (cartDrawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [cartDrawerOpen]);

  const updateAccountMenuPosition = () => {
    const btn = accountButtonRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    setAccountMenuPos({
      top: r.bottom + 8,
      right: Math.max(8, window.innerWidth - r.right),
    });
  };

  useLayoutEffect(() => {
    if (!accountMenuOpen) return;
    updateAccountMenuPosition();
  }, [accountMenuOpen]);

  useEffect(() => {
    if (!accountMenuOpen) return;
    const onScrollOrResize = () => updateAccountMenuPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [accountMenuOpen]);

  useEffect(() => {
    if (!accountMenuOpen) return;
    const onDown = (e) => {
      const inTrigger = accountMenuRef.current?.contains(e.target);
      const inMenu = accountDropdownRef.current?.contains(e.target);
      if (!inTrigger && !inMenu) {
        setAccountMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [accountMenuOpen]);

  const navLinkClass = (path, exactOverride) => {
    const active = exactOverride !== undefined ? exactOverride : isActive(path);
    return `rounded-full px-4 py-2 text-sm font-semibold transition ${
      active
        ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md shadow-orange-300/35"
        : "text-stone-600 hover:bg-orange-50 hover:text-orange-800"
    }`;
  };

  /** Same tile style for wishlist, bag, account; “active” = open menu / open cart / on wishlist (not dashboard route) */
  const iconBtnClass = (active) =>
    [
      "relative flex h-11 min-w-[2.75rem] items-center justify-center rounded-xl border text-stone-600 transition outline-none",
      "focus-visible:ring-2 focus-visible:ring-orange-400/50 focus-visible:ring-offset-2 focus-visible:ring-offset-white",
      active
        ? "border-orange-300 bg-orange-50 text-orange-800 shadow-md"
        : "border-stone-200/70 bg-white/90 shadow-sm hover:border-orange-200 hover:bg-orange-50/90 hover:text-orange-800",
    ].join(" ");

  return (
    <header className="sticky top-0 z-50 border-b border-orange-100/90 bg-white/95 shadow-sm backdrop-blur-md">
      <div className="mx-auto hidden max-w-7xl items-center gap-6 px-5 py-3 md:grid md:grid-cols-[auto_minmax(0,1fr)_auto] md:px-6 md:py-3.5">
        <div className="flex min-w-0 items-center gap-6">
          <Link
            to={rootLink}
            className="shrink-0 text-2xl font-bold tracking-tight text-orange-600 transition hover:text-orange-700"
          >
            {(loadingStorefront && !vendorStore) ? (
              <div className="h-16 w-16 animate-pulse rounded-2xl bg-stone-200" />
            ) : logoSrc ? (
              <img
                src={logoSrc}
                alt={logoAlt}
                className="h-16 w-16 rounded-2xl border-2 border-orange-100 object-cover shadow-md ring-1 ring-black/5"
              />
            ) : (
              <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 text-lg font-extrabold text-white shadow-md">
                {vendorStore ? (vendorStore.storeName || vendorStore.name || "S").charAt(0).toUpperCase() : "MMD"}
              </span>
            )}
          </Link>

          <nav className="flex items-center gap-1" aria-label="Primary">
            <Link
              to={shopLink}
              className={navLinkClass(
                "/shop",
                storeSlug
                  ? location.pathname === `/${storeSlug}` || location.pathname === `/${storeSlug}/`
                  : undefined,
              )}
            >
              Shop
            </Link>
            <Link to={aboutLink} className={navLinkClass(storeSlug ? `/${storeSlug}/about` : "/about")}>
              About
            </Link>
            <Link to={contactLink} className={navLinkClass(storeSlug ? `/${storeSlug}/contact` : "/contact")}>
              Contact
            </Link>
          </nav>
        </div>

        <div className="flex min-w-0 justify-center px-2">
          <SearchFilter maxWidthClass="w-full max-w-md lg:max-w-xl" wrapperClassName="px-0" />
        </div>

        <div className="flex shrink-0 items-center justify-end gap-1.5 sm:gap-2">
          {isUserLoggedIn && wishlistEnabled && (
            <Link
              to="/wishlist"
              className={iconBtnClass(isActive("/wishlist"))}
              title="Wishlist"
            >
              <Heart className="h-5 w-5 shrink-0 text-current" strokeWidth={2.25} />
            </Link>
          )}

          {isVendorLoggedIn && (
            <Link
              to="/vendor/dashboard"
              className="rounded-full border border-teal-200 bg-teal-50 px-3 py-2 text-xs font-bold text-teal-800 transition hover:bg-teal-100"
            >
              Vendor
            </Link>
          )}

          <button
            type="button"
            onClick={() => setCartDrawerOpen(!cartDrawerOpen)}
            className={`hidden md:flex ${iconBtnClass(cartDrawerOpen || isActive("/checkout"))}`}
            title="Shopping bag"
          >
            <span className="relative inline-flex items-center justify-center pr-1.5 pt-0.5">
              <ShoppingBag className="h-5 w-5 shrink-0 text-current" strokeWidth={2.25} />
              {cart.length > 0 && (
                <span className="absolute right-0 top-0 flex h-[1.125rem] min-w-[1.125rem] translate-x-1/3 -translate-y-1/3 items-center justify-center rounded-full bg-gradient-to-br from-red-500 to-rose-600 px-0.5 text-[9px] font-bold leading-none text-white shadow ring-2 ring-white">
                  {cart.length > 9 ? "9+" : cart.length}
                </span>
              )}
            </span>
          </button>

          {isUserLoggedIn ? (
            <div className="relative" ref={accountMenuRef}>
              <button
                ref={accountButtonRef}
                type="button"
                onClick={() => setAccountMenuOpen((o) => !o)}
                className={`${iconBtnClass(accountMenuOpen)} flex w-auto gap-1 px-2.5`}
                aria-expanded={accountMenuOpen}
                aria-haspopup="true"
                title="Account menu"
              >
                <UserRound className="h-5 w-5 shrink-0 text-current" strokeWidth={2.25} />
                <span className="text-current" aria-hidden>
                  {accountMenuOpen ? (
                    <ChevronUp className="h-4 w-4 shrink-0 opacity-80" strokeWidth={2.5} />
                  ) : (
                    <ChevronDown className="h-4 w-4 shrink-0 opacity-80" strokeWidth={2.5} />
                  )}
                </span>
              </button>
              {accountMenuOpen &&
                createPortal(
                  <div
                    ref={accountDropdownRef}
                    className="fixed z-[10050] w-52 overflow-hidden rounded-xl border border-stone-200/90 bg-white py-1 shadow-xl ring-1 ring-black/5"
                    style={{ top: accountMenuPos.top, right: accountMenuPos.right }}
                    role="menu"
                  >
                    <Link
                      role="menuitem"
                      to="/user/dashboard"
                      className="block px-4 py-2.5 text-sm font-medium text-stone-800 hover:bg-orange-50"
                      onClick={() => setAccountMenuOpen(false)}
                    >
                      My account
                    </Link>
                    <button
                      type="button"
                      role="menuitem"
                      className="flex w-full items-center gap-2 border-t border-stone-100 px-4 py-2.5 text-left text-sm font-semibold text-red-600 hover:bg-red-50"
                      onClick={() => {
                        setAccountMenuOpen(false);
                        handleLogout();
                      }}
                    >
                      <LogOut className="h-4 w-4 shrink-0" />
                      Sign out
                    </button>
                  </div>,
                  document.body,
                )}
            </div>
          ) : isVendorLoggedIn ? (
            <button
              type="button"
              onClick={handleLogout}
              className="rounded-full border border-stone-200 px-3 py-2 text-xs font-semibold text-stone-600 transition hover:bg-stone-50"
            >
              Sign out
            </button>
          ) : (
            <Link
              to="/user-login"
              state={{ from: `${location.pathname}${location.search}` }}
              className="rounded-full bg-gradient-to-r from-orange-500 to-amber-500 px-4 py-2 text-sm font-bold text-white shadow-md shadow-orange-300/40 transition hover:from-orange-600 hover:to-amber-600"
            >
              Log in
            </Link>
          )}
        </div>
      </div>

      <div className="md:hidden px-4 py-3 bg-gradient-to-r from-pink-50 via-purple-50 to-blue-50 border-b border-pink-100">
        <div className="flex min-w-0 items-center gap-2">
          <Link
            to={rootLink}
            className="shrink-0 text-2xl font-bold text-pink-600 tracking-tight"
          >
            {(loadingStorefront && !vendorStore) ? (
              <div className="h-12 w-12 bg-gray-200 rounded-full animate-pulse" />
            ) : logoSrc ? (
              <img
                src={logoSrc}
                alt={logoAlt}
                className="h-12 w-12 rounded-full object-cover border-2 border-pink-200 shadow-sm"
              />
            ) : (
              vendorStore
                ? (vendorStore.storeName || vendorStore.name || "S").charAt(0).toUpperCase()
                : "MMD"
            )}
          </Link>

          <div className="min-w-0 flex-1">
            <SearchFilter />
          </div>

          <button
            type="button"
            onClick={() => setCartDrawerOpen((open) => !open)}
            className={`shrink-0 ${iconBtnClass(cartDrawerOpen || isActive("/checkout"))}`}
            title="Shopping bag"
            aria-expanded={cartDrawerOpen}
            aria-label="Open shopping bag"
          >
            <span className="relative inline-flex items-center justify-center pr-1.5 pt-0.5">
              <ShoppingBag className="h-5 w-5 shrink-0 text-current" strokeWidth={2.25} />
              {cart.length > 0 && (
                <span className="absolute right-0 top-0 flex h-[1.125rem] min-w-[1.125rem] translate-x-1/3 -translate-y-1/3 items-center justify-center rounded-full bg-gradient-to-br from-red-500 to-rose-600 px-0.5 text-[9px] font-bold leading-none text-white shadow ring-2 ring-white">
                  {cart.length > 9 ? "9+" : cart.length}
                </span>
              )}
            </span>
          </button>
        </div>
      </div>

      <nav
        className="fixed bottom-0 left-0 right-0 z-50 border-t border-orange-200/40 bg-gradient-to-b from-white/95 to-orange-50/90 px-2 pt-2 shadow-[0_-8px_32px_rgba(234,88,12,0.12)] backdrop-blur-xl md:hidden pb-[max(0.5rem,env(safe-area-inset-bottom))]"
        aria-label="Main mobile navigation"
      >
        <div className="mx-auto flex max-w-lg items-stretch justify-between gap-1">
          <Link
            to={shopLink}
            className={`flex min-h-[52px] min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-2xl px-1 py-1.5 transition active:scale-[0.97] ${
              (storeSlug
                ? location.pathname === `/${storeSlug}` || location.pathname === `/${storeSlug}/`
                : isActive("/shop"))
                ? "bg-gradient-to-br from-orange-500 to-amber-500 text-white shadow-lg shadow-orange-300/45"
                : "text-stone-600 hover:bg-white/80"
            }`}
          >
            <Store
              className={`h-6 w-6 shrink-0 ${
                (storeSlug
                  ? location.pathname === `/${storeSlug}` || location.pathname === `/${storeSlug}/`
                  : isActive("/shop"))
                  ? "text-white"
                  : "text-orange-500"
              }`}
              strokeWidth={2.25}
            />
            <span className="text-[10px] font-bold uppercase tracking-wide">Shop</span>
          </Link>

          {wishlistEnabled && (
            <Link
              to="/wishlist"
              className={`flex min-h-[52px] min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-2xl px-1 py-1.5 transition active:scale-[0.97] ${
                isActive("/wishlist")
                  ? "bg-gradient-to-br from-rose-500 to-pink-500 text-white shadow-lg shadow-rose-300/40"
                  : "text-stone-600 hover:bg-white/80"
              }`}
            >
              <Heart
                className={`h-6 w-6 shrink-0 ${isActive("/wishlist") ? "text-white" : "text-rose-500"}`}
                strokeWidth={2.25}
              />
              <span className="text-[10px] font-bold uppercase tracking-wide">Wishlist</span>
            </Link>
          )}

          {isUserLoggedIn ? (
            <Link
              to="/user/dashboard"
              className={`flex min-h-[52px] min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-2xl px-1 py-1.5 transition active:scale-[0.97] ${
                isActive("/user/dashboard")
                  ? "bg-gradient-to-br from-violet-600 to-indigo-600 text-white shadow-lg shadow-violet-400/35"
                  : "text-stone-600 hover:bg-white/80"
              }`}
            >
              <UserRound
                className={`h-6 w-6 shrink-0 ${isActive("/user/dashboard") ? "text-white" : "text-violet-600"}`}
                strokeWidth={2.25}
              />
              <span className="text-[10px] font-bold uppercase tracking-wide">Account</span>
            </Link>
          ) : (
            <Link
              to="/user-login"
              state={{ from: `${location.pathname}${location.search}` }}
              className={`flex min-h-[52px] min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-2xl px-1 py-1.5 transition active:scale-[0.97] ${
                isActive("/user-login")
                  ? "bg-gradient-to-br from-violet-600 to-indigo-600 text-white shadow-lg shadow-violet-400/35"
                  : "text-stone-600 hover:bg-white/80"
              }`}
            >
              <UserRound
                className={`h-6 w-6 shrink-0 ${isActive("/user-login") ? "text-white" : "text-violet-600"}`}
                strokeWidth={2.25}
              />
              <span className="text-[10px] font-bold uppercase tracking-wide">Login</span>
            </Link>
          )}
        </div>
      </nav>

      <CartDrawer
        showCart={cartDrawerOpen}
        setShowCart={setCartDrawerOpen}
        cart={cart}
        updateQuantity={updateQuantity}
        variant={isMobile ? "bottom" : "right"}
      />
    </header>
  );
};

export default Header;
